import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircleIcon, CheckCircle2Icon } from 'lucide-react';
import { RoadMark } from '@/components/brand/RoadMark';
import { LocaleToggle } from '@/components/LocaleToggle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** The lockup: the road mark, `CoreWay` in ink and `Partners` in the accent (the portal Wordmark's shape). */
export function PartnersMark() {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 items-center gap-2">
      <RoadMark className="size-8 shrink-0 text-accent-base" laneClassName="fill-card" />
      <span className="flex min-w-0 items-baseline gap-1.5 text-lg font-bold tracking-tight">
        <span className="truncate text-ink">{t('promoter.brand.name')}</span>
        <span className="truncate text-accent-base">{t('promoter.brand.product')}</span>
      </span>
    </div>
  );
}

/**
 * Every signed-in screen: a slim top bar (mark, language, sign out) over one centred column. Mobile first:
 * one column at 360 px, the same column capped at 48 rem on a desktop.
 */
export function Shell({ onSignOut, children, wide = false }: { onSignOut?: () => void; children: React.ReactNode; wide?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-full bg-canvas">
      <header className="border-b border-hairline bg-card">
        <div className={cn('mx-auto flex items-center justify-between gap-3 px-4 py-2', wide ? 'max-w-3xl' : 'max-w-xl')}>
          <PartnersMark />
          <div className="flex shrink-0 items-center gap-2">
            <LocaleToggle />
            {onSignOut && (
              <Button variant="ghost" size="sm" onClick={onSignOut}>
                {t('promoter.common.signOut')}
              </Button>
            )}
          </div>
        </div>
      </header>
      <main className={cn('mx-auto flex flex-col gap-4 px-4 py-6', wide ? 'max-w-3xl' : 'max-w-xl')}>{children}</main>
    </div>
  );
}

/** A translated message in a form or card: `danger` for refusals (role=alert), `success` for confirmations. */
export function Notice({ tone = 'danger', children, className }: { tone?: 'danger' | 'success' | 'warning'; children: React.ReactNode; className?: string }) {
  if (!children) return null;
  const Icon = tone === 'success' ? CheckCircle2Icon : AlertCircleIcon;
  return (
    <div
      role={tone === 'success' ? 'status' : 'alert'}
      className={cn(
        'flex items-start gap-3 rounded-md border bg-card px-4 py-3 text-sm',
        tone === 'danger' && 'border-danger/40 text-danger',
        tone === 'success' && 'border-success/40 text-success',
        tone === 'warning' && 'border-warning/40 text-ink',
        className,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">{children}</p>
    </div>
  );
}

/** A titled block of the dashboard (a `section` labelled by its heading). */
export function Section({ id, title, children, action }: { id: string; title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section aria-labelledby={`${id}-title`} className="rounded-lg border border-hairline bg-card p-4 shadow-card sm:p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 id={`${id}-title`} className="text-lg font-semibold text-ink">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}
